import { Autocomplete, Box, Chip, TextField, Tooltip, Typography, alpha } from "@mui/material";
import { AutoAwesome as SuggestedIcon } from "@mui/icons-material";
import { useMemo, type HTMLAttributes, type Key } from "react";

/** A single selectable target — a field within a specific schema. */
export interface UnifiedTarget {
  schema: string;
  field: string;
  label: string;
  /** Group header in the dropdown (schema display name). */
  group: string;
  /** True when this target doesn't resolve in the schema registry (#6425): a
   *  legacy/drifted mapping to a nonexistent field. Rendered as an error chip so
   *  it isn't mistaken for a valid mapping. */
  isUnknown?: boolean;
}

/** Required/recommended cue for an option, keyed by `schema:field` elsewhere. */
export interface CriticalCue {
  severity: "required" | "recommended";
  impact: string;
}

export const targetKey = (o: { schema: string; field: string }) => `${o.schema}:${o.field}`;

interface TargetFieldSelectProps {
  /** Union of every schema's fields (schema-tagged), prebuilt by the host. */
  options: UnifiedTarget[];
  /** Required/recommended cues keyed `schema:field`. */
  criticalByKey: Map<string, CriticalCue>;
  /** Currently selected target(s) for this row. */
  value: UnifiedTarget[];
  onChange: (targets: { schema: string; field: string }[]) => void;
  /** Best-guess `schema:field` for this row's source — floated to the top. */
  suggestedKey?: string | null;
  /** Fields already claimed by OTHER rows — disabled so a duplicate can't be
   *  created (this row's own selections stay enabled). */
  claimedKeys?: Set<string>;
  placeholder?: string;
}

/**
 * Shared cross-schema "Maps to" dropdown (#6118, extracted from the horizontal
 * remap grid). Offers fields from every target schema, grouped by schema with
 * standout headers; floats the Suggested match to the top and tags Required /
 * Recommended fields from the critical-fields contract; disables already-claimed
 * fields to prevent duplicates. Reused by the spreadsheet grid and the PDF
 * map-on-document surface so both mapping experiences stay identical.
 */
export const TargetFieldSelect = ({
  options,
  criticalByKey,
  value,
  onChange,
  suggestedKey = null,
  claimedKeys,
  placeholder = "Map to…",
}: TargetFieldSelectProps) => {
  const rank = (key: string) => {
    const c = criticalByKey.get(key);
    return c ? (c.severity === "required" ? 0 : 1) : 2;
  };

  const currentKeys = useMemo(() => new Set(value.map(targetKey)), [value]);

  // Option order within each schema group: the suggested field first, then
  // required, then recommended, then the rest alphabetically.
  const orderedOptions = useMemo(() => {
    return [...options].sort((a, b) => {
      const g = a.group.localeCompare(b.group);
      if (g) return g;
      const sa = targetKey(a) === suggestedKey ? 0 : 1;
      const sb = targetKey(b) === suggestedKey ? 0 : 1;
      if (sa !== sb) return sa - sb;
      const r = rank(targetKey(a)) - rank(targetKey(b));
      if (r) return r;
      return a.label.localeCompare(b.label);
    });
  }, [options, suggestedKey, criticalByKey]);

  const isMapped = value.length > 0;

  return (
    <Autocomplete
      multiple
      fullWidth
      size="small"
      options={orderedOptions}
      value={value}
      onChange={(_e, newValue) => onChange(newValue.map((v) => ({ schema: v.schema, field: v.field })))}
      groupBy={(o) => o.group}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(o, v) => o.schema === v.schema && o.field === v.field}
      // Disable fields already mapped by another row so the reviewer can't
      // create a duplicate (this row's own selections stay enabled).
      getOptionDisabled={
        claimedKeys
          ? (o) => {
              const k = targetKey(o);
              return claimedKeys.has(k) && !currentKeys.has(k);
            }
          : undefined
      }
      limitTags={1}
      // The dropdown menu is decoupled from the (possibly narrow) control so the
      // option label + Required/Suggested cues have room to breathe.
      slotProps={{
        paper: {
          sx: {
            minWidth: 320,
            bgcolor: "background.paper",
            "& .MuiAutocomplete-listbox": {
              backgroundColor: (theme) => theme.palette.background.paper,
            },
            // Opaque tinted group headers so options scrolling beneath the
            // sticky header don't show through.
            "& .MuiAutocomplete-groupLabel": {
              backgroundColor: (theme) => theme.palette.background.paper,
              backgroundImage: (theme) =>
                `linear-gradient(${alpha(theme.palette.primary.main, 0.12)}, ${alpha(theme.palette.primary.main, 0.12)})`,
              color: "text.primary",
              fontWeight: 700,
              fontSize: "0.7rem",
              textTransform: "uppercase",
              letterSpacing: 0.6,
              lineHeight: "28px",
            },
          },
        },
        popper: { style: { width: "fit-content" } },
      }}
      renderOption={(props, option) => {
        const { key, ...liProps } = props as HTMLAttributes<HTMLLIElement> & { key?: Key };
        const oKey = targetKey(option);
        const crit = criticalByKey.get(oKey);
        const isSuggested = oKey === suggestedKey;
        return (
          <li key={key} {...liProps}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, width: "100%" }}>
              <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
                {option.label}
              </Typography>
              {isSuggested && (
                <Chip
                  icon={<SuggestedIcon sx={{ fontSize: "0.8rem !important" }} />}
                  label="Suggested"
                  size="small"
                  color="info"
                  variant="outlined"
                  sx={{ height: 18, flexShrink: 0, "& .MuiChip-label": { px: 0.5, fontSize: "0.62rem" } }}
                />
              )}
              {crit && (
                <Tooltip title={crit.impact} arrow placement="right">
                  <Chip
                    label={crit.severity === "required" ? "Required" : "Recommended"}
                    size="small"
                    color={crit.severity === "required" ? "error" : "warning"}
                    variant="outlined"
                    sx={{ height: 18, flexShrink: 0, "& .MuiChip-label": { px: 0.5, fontSize: "0.62rem" } }}
                  />
                </Tooltip>
              )}
            </Box>
          </li>
        );
      }}
      // Fixed-height, single-line control so every box is the same height whether
      // empty, mapped, or focused; chip type scaled down (truncates with a
      // tooltip) so the field name stays legible in a narrow column.
      sx={{
        "& .MuiInputBase-root": {
          flexWrap: "nowrap",
          height: 34,
          minHeight: 34,
          py: 0,
          pr: "26px !important",
          fontSize: "0.7rem",
        },
        "& input": { minWidth: "0 !important" },
        "& .MuiChip-root": { height: 20, maxWidth: "100%" },
        "& .MuiChip-label": { fontSize: "0.66rem", px: 0.5 },
        "& .MuiAutocomplete-tag": { m: 0, maxWidth: "100%" },
        "& .MuiAutocomplete-endAdornment": { right: 2 },
        "& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator": { p: 0.25 },
        "& .MuiAutocomplete-endAdornment .MuiSvgIcon-root": { fontSize: "1rem" },
      }}
      renderInput={(params) => (
        <TextField {...params} variant="outlined" size="small" placeholder={isMapped ? "" : placeholder} />
      )}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const tagProps = getTagProps({ index });
          const isUnknown = Boolean(option.isUnknown);
          return (
            <Tooltip
              key={targetKey(option)}
              title={
                isUnknown
                  ? `"${option.label}" is not a valid field for this schema — remap this target.`
                  : `${option.group} · ${option.label}`
              }
              arrow
            >
              {/* onDelete={undefined} drops the chip's own ✕ — the dropdown arrow
                  (and clear-on-hover) already handle changing/removing, so the
                  label keeps the width. An unknown target renders as a filled
                  error chip (#6425) so it can't be mistaken for a valid mapping. */}
              <Chip
                {...tagProps}
                onDelete={undefined}
                label={option.label}
                size="small"
                color={isUnknown ? "error" : "primary"}
                variant={isUnknown ? "filled" : "outlined"}
              />
            </Tooltip>
          );
        })
      }
    />
  );
};
